import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { IconType } from "react-icons/lib";
import { MaskInput, MaskInputOptions } from "maska";

import { cn } from "@shared/utils/cn";

import { MinimalFormField, Resolver } from "../types/form-fields";

export interface InputTextOptions {
  className?: string;
  containerClassName?: string;
  min?: number | string;
  max?: number | string;
  minLength?: number;
  maxLength?: number;
  step?: number;
  placeholder?: string;
  autoFocus?: boolean;
  readonly?: boolean;
  LeftIcon?: IconType;
  mask?: string;
  maskTokens?: {
    [key: string]: {
      pattern: RegExp;
      transform?: (chr: string) => string;
      optional?: boolean;
      repeated?: boolean;
      multiple?: boolean;
    };
  };
  maskOptions?: MaskInputOptions;
}

export interface InputTextProps extends MinimalFormField<string> {
  type:
    | "text"
    | "date"
    | "datetime"
    | "datetime-local"
    | "number"
    | "email"
    | "search"
    | "time";
  options?: InputTextOptions;
}

export const InputText = forwardRef<object, InputTextProps>(
  ({ label, error, onChange, options = {}, defaultValue, ...props }, ref) => {
    const innerRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => innerRef.current);

    useEffect(() => {
      const maskOption = options.mask
        ? {
            mask: options.mask,
            tokens: {
              A: {
                pattern: /[A-Z]/,
                transform: (chr: string) => chr.toUpperCase(),
              },
              a: {
                pattern: /[a-z]/,
                transform: (chr: string) => chr.toLowerCase(),
              },
              ...(options.maskTokens ?? {}),
              ...(options.maskOptions?.tokens ?? {}),
            },
            ...options.maskOptions,
          }
        : undefined;

      if (!maskOption) {
        return;
      }

      const mask = new MaskInput(innerRef.current, {
        ...maskOption,
        onMaska: (detail) => {
          onChange(detail.masked, detail);
        },
      });

      return () => {
        mask.destroy();
      };
    }, [innerRef]);

    return (
      <div
        className={cn(
          "flex flex-col transition-colors relative",
          options.containerClassName
        )}
      >
        {label && (
          <label
            htmlFor=""
            className={cn(
              "block text-black/80 font-poppins text-sm font-medium mb-2",
              error && "text-red"
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
        {options.LeftIcon &&
          options.LeftIcon?.({
            className:
              "absolute text-xl left-4 top-1/2 -translate-y-1/2 text-gray800/40",
          })}
        <input
          className={cn(
            "text-black/80 font-inter w-full py-[9px] px-[13px] focus:outline-none rounded-lg border border-black/20 disabled:cursor-not-allowed",
            error && "border-red",
            options.className,
            options.LeftIcon && "pl-12"
          )}
          ref={innerRef}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options.placeholder}
          min={options.min}
          max={options.max}
          minLength={options.minLength}
          maxLength={options.maxLength}
          step={options.step}
          inputMode={options.inputMode}
          readOnly={options.readOnly}
          autoFocus={
            !options.readOnly && !props.readOnly ? options.autoFocus : false
          }
          {...props}
        />
        </div>
        {error && <p className="text-red text-xs italic mt-1">{error}</p>}
      </div>
    );
  }
);

InputText.displayName = "InputText";

export const inputTextResolver: Resolver<InputTextProps> = {
  component: InputText,
  defaultValue: "",
  type: "text",
};
